import { FileArrowUpIcon, InfoIcon } from '@phosphor-icons/react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { FileMimeTypes, GenericTable, Spinner } from '@/modules/common';
import { useEventFilters } from '@/modules/eventsV2/hooks';

import {
  CreateStationDialog,
  InventoryActions,
  InventoryDetailsDialog,
  InventoryTableFilters,
} from './components';
import { useInventoryTab } from './hooks/use-inventory-tab';

export function InventoryTab() {
  const fileInputRef = useRef(null);

  const [isCreateStationDialogOpen, setIsCreateStationDialogOpen] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const { filters, filtersChange } = useEventFilters();

  const {
    eventId,
    products,
    columns,
    stationsQuery,
    isFetching,
    isUploading,
    selectedStationId,
    isGeneratingReport,
    setSelectedStationId,
    pickFileAndUpload,
    getGenerateReportUrl,
  } = useInventoryTab(filters);

  const pickFile = () => {
    fileInputRef.current.click();
  };

  if (isFetching || isUploading || stationsQuery.isFetching) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="font-semibold text-lg">Stations Table</p>
      <div className="flex flex-col gap-4">
        <div className="py-5 flex gap-5 items-center justify-between bg-zinc-900 rounded-lg border border-zinc-800 p-4">
          <div className="flex gap-2 items-center">
            <InfoIcon />
            <div className="text-xs">
              <p className="font-semibold">Manage existing stations</p>
              <p>Use the "Stations" button to view or update them.</p>
            </div>
          </div>

          <Button onClick={() => setIsCreateStationDialogOpen(true)}>
            Stations
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {stationsQuery.data.map((station) => (
            <Button
              onClick={() => setSelectedStationId(station.id)}
              variant={selectedStationId === station.id ? 'default' : 'outline'}
            >
              {station.name}
            </Button>
          ))}
        </div>

        <GenericTable
          columns={columns}
          name="inventory"
          data={products}
          emptyCellClassName="h-20"
          emptyTitle="Upload inventory from CSV file."
          emptyDescription="The inventory will be uploaded here. Please upload new ones."
          tableRowClassName="hover:bg-transparent"
          onClick={(item) => setSelectedRow(item)}
          isFiltered={Object.keys(filters).length > 0}
          disabledColumnsIdsToClick={["totalAmount", "actions"]}
          emptyButton={
            <Button
              disabled={isFetching}
              type="button"
              onClick={pickFile}
              data-testid="upload-inventory-button"
            >
              <FileArrowUpIcon />
              Upload CSV
              <input
                id="inventory-file"
                type="file"
                ref={fileInputRef}
                className="hidden"
                data-testid="upload-inventory-file-input"
                onChange={pickFileAndUpload}
                accept={FileMimeTypes.Csv}
              />
            </Button>
          }
        >
          <div className="flex gap-4 items-center justify-center">
            <InventoryTableFilters
              filters={filters}
              onFilters={filtersChange}
            />

            <div className="flex gap-2">
              <Button
                disabled={isFetching}
                variant="outline"
                type="button"
                onClick={pickFile}
                data-testid="upload-inventory-button"
              >
                <FileArrowUpIcon />
                Upload CSV
                <input
                  id="inventory-file"
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  data-testid="upload-inventory-file-input"
                  onChange={pickFileAndUpload}
                  accept={FileMimeTypes.Csv}
                />
              </Button>

              {products.length > 0 && (
                <InventoryActions
                  eventId={eventId}
                  stationId={selectedStationId}
                  onGenerateReport={getGenerateReportUrl}
                  isGeneratingReport={isGeneratingReport}
                />
              )}
            </div>
          </div>
        </GenericTable>
      </div>

      <CreateStationDialog
        isOpen={isCreateStationDialogOpen}
        eventId={eventId}
        stations={stationsQuery.data || []}
        onOpenChange={setIsCreateStationDialogOpen}
      />

      {selectedRow && (
        <InventoryDetailsDialog
          isOpen={!!selectedRow}
          onOpenChange={() => setSelectedRow(null)}
          selectedInventory={selectedRow}
          eventId={eventId}
          stationId={selectedStationId}
        />
      )}
    </div>
  );
}
